import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { APIProvider, Map, Marker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import axios from 'axios';
import { Button } from './ui/button';
import { X, MapPin, Search, Loader2, RefreshCw, Sparkles, Route, Warehouse, Navigation, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Região de trabalho: GA / TN / SC
const REGION_CENTER = { lat: 34.3, lng: -83.6 };
const REGION_ZOOM = 6;

const TEAM_COLORS = {
  PM1: '#e11d48', PM2: '#2563eb', PM3: '#16a34a', PM4: '#d97706', PM5: '#7c3aed',
  PM6: '#0891b2', PM7: '#db2777', PM8: '#4d7c0f', PM9: '#b91c1c', PM10: '#0d9488',
  MEASURE: '#1f2937',
};
const colorForTeam = (t) => TEAM_COLORS[t] || '#64748b';
// Medições (job_type MEASURE, sem equipe) entram no mapa como a pseudo-equipe "MEASURE" (filtro, pino, legenda)
const MEASURE_TEAM = 'MEASURE';
const teamLabel = (t) => (t === MEASURE_TEAM ? '📏 Measure' : t);
const normalizeJobs = (list) => (list || []).map((j) => (j.job_type === 'MEASURE' && !j.team ? { ...j, team: MEASURE_TEAM } : j));

// Pino em formato de etiqueta com o nome da equipe SEMPRE visível (sem precisar clicar)
const teamTagIcon = (team, color, dateStr = '', multi = false) => {
  const label = (team || '?').toString();
  const date = (dateStr || '').toString();
  const textLen = Math.max(label.length, date.length);
  const w = Math.max(38, 12 + textLen * 7);
  const half = w / 2;
  const stroke = multi ? '#f59e0b' : '#ffffff';
  const sw = multi ? 3 : 1.5;
  if (!date) {
    const svg1 = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="34" viewBox="0 0 ${w} 34"><rect x="1.5" y="1.5" width="${w - 3}" height="21" rx="6" fill="${color}" stroke="${stroke}" stroke-width="${sw}"/><polygon points="${half - 6},22 ${half + 6},22 ${half},32" fill="${color}"/><text x="${half}" y="16.5" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle">${label}</text></svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg1);
  }
  // Etiqueta compacta em 2 linhas: equipe (topo) + data (embaixo) — pequena para não tampar o mapa
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="42" viewBox="0 0 ${w} 42"><rect x="1.5" y="1.5" width="${w - 3}" height="29" rx="6" fill="${color}" stroke="${stroke}" stroke-width="${sw}"/><polygon points="${half - 6},30 ${half + 6},30 ${half},40" fill="${color}"/><text x="${half}" y="14" font-family="Arial, sans-serif" font-size="11" font-weight="700" fill="#ffffff" text-anchor="middle">${label}</text><text x="${half}" y="25.5" font-family="Arial, sans-serif" font-size="9" font-weight="600" fill="#ffffff" text-anchor="middle">${date}</text></svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};
const candidateIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="62" viewBox="0 0 48 62"><defs><filter id="sh" x="-30%" y="-20%" width="160%" height="150%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.35"/></filter></defs><g filter="url(#sh)"><path d="M24 1C12 1 2 10.6 2 22.5 2 39 22 60 23 61c.6.6 1.5.6 2 0C26 60 46 39 46 22.5 46 10.6 36 1 24 1z" fill="#dc2626" stroke="#ffffff" stroke-width="2.5"/><circle cx="24" cy="22" r="11" fill="#ffffff"/><path d="M24 15l7 6v9h-4v-6h-6v6h-4v-9z" fill="#dc2626"/></g></svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// Badge circular A/B para os pinos selecionados na comparação de distância
const compareBadgeIcon = (letter) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30"><defs><filter id="cs" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="#000" flood-opacity="0.4"/></filter></defs><g filter="url(#cs)"><circle cx="15" cy="15" r="12" fill="#f59e0b" stroke="#ffffff" stroke-width="2.5"/><text x="15" y="20" font-size="15" font-family="Arial" text-anchor="middle" fill="#ffffff" font-weight="700">${letter}</text></g></svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// Warehouses de carregamento (coordenadas geocodificadas) - para a Rota do Dia
const WAREHOUSES = {
  Lawrenceville: { lat: 33.9721417, lng: -84.0884388 },
  Macon: { lat: 32.9305971, lng: -83.7114449 },
  Pooler: { lat: 32.1648969, lng: -81.267421 },
};

const todayStr = (d) => {
  const x = d || new Date();
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
// Formata minutos como "2h 28min" (ou "45min" quando < 1h)
const fmtDuration = (mins) => {
  const m = Math.round(mins || 0);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h}h` : `${h}h ${rest}min`;
};
const firstOfMonth = () => { const n = new Date(); return todayStr(new Date(n.getFullYear(), n.getMonth(), 1)); };
const endOfNextMonth = () => { const n = new Date(); return todayStr(new Date(n.getFullYear(), n.getMonth() + 2, 0)); };
// "01/09–30/09" para o botão Filtro
const fmtRangeShort = (from, to) => {
  const f = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '?');
  return from === to ? f(from) : `${f(from)}–${f(to)}`;
};
// Fim do mês N meses à frente (para o padrão da simulação da IA)
const monthsAheadEnd = (n) => { const x = new Date(); return todayStr(new Date(x.getFullYear(), x.getMonth() + n + 1, 0)); };

// Data compacta para o pino: "5/2", "5-7/2" (multi-dia mesmo mês) ou "28/1+"
const fmtPinDate = (j) => {
  const dates = (j.dates && j.dates.length) ? j.dates : [j.date];
  const valid = dates.filter(Boolean).slice().sort();
  if (valid.length === 0) return '';
  const parse = (d) => { const p = String(d).split('-'); return { m: parseInt(p[1], 10), d: parseInt(p[2], 10) }; };
  const first = parse(valid[0]);
  if (valid.length === 1) return `${first.d}/${first.m}`;
  const last = parse(valid[valid.length - 1]);
  return first.m === last.m ? `${first.d}-${last.d}/${first.m}` : `${first.d}/${first.m}+`;
};

// Ajusta o mapa para enquadrar os pins — SÓ quando `triggerKey` muda (evita zoom indesejado ao clicar/fechar)
const FitBounds = ({ points, triggerKey, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google || !points || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter({ lat: points[0].lat, lng: points[0].lng });
      map.setZoom(zoom || 11);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 80);
  }, [map, triggerKey]);
  return null;
};

// Desenha a rota otimizada do dia: warehouse (A) -> casas do cliente na melhor ordem
const DayRoute = ({ enabled, warehouse, stops, onInfo }) => {
  const map = useMap();
  const routesLib = useMapsLibrary('routes');
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!map || !routesLib) return undefined;
    if (!rendererRef.current) {
      rendererRef.current = new routesLib.DirectionsRenderer({
        suppressMarkers: false,
        preserveViewport: false,
        polylineOptions: { strokeColor: '#4338ca', strokeWeight: 5, strokeOpacity: 0.85 },
      });
    }
    const renderer = rendererRef.current;
    if (!enabled || !warehouse || !stops || stops.length === 0) {
      renderer.setMap(null);
      if (onInfo) onInfo(null);
      return undefined;
    }
    renderer.setMap(map);
    const svc = new routesLib.DirectionsService();
    // A ordem das paradas segue o HORÁRIO de início (já vem ordenado do componente pai).
    // Não otimizamos por proximidade — respeitamos a sequência do primeiro ao último trabalho.
    const ordered = stops;
    const destination = ordered[ordered.length - 1];
    const waypoints = ordered.slice(0, -1);
    let cancelled = false;
    svc.route(
      {
        origin: { lat: warehouse.lat, lng: warehouse.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        waypoints: waypoints.map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true })),
        optimizeWaypoints: false,
        travelMode: 'DRIVING',
      },
      (res, status) => {
        if (cancelled) return;
        if (status === 'OK' && res.routes && res.routes[0]) {
          renderer.setDirections(res);
          const route = res.routes[0];
          let dist = 0;
          let dur = 0;
          route.legs.forEach((l) => { dist += l.distance.value; dur += l.duration.value; });
          // Cada leg[i] chega na parada ordered[i] (warehouse -> ordered[0] -> ordered[1] -> ...)
          const legs = route.legs.map((l, i) => ({
            stop: ordered[i] || null,
            distance_mi: (l.distance.value / 1609.34).toFixed(1),
            minutes: Math.round(l.duration.value / 60),
          }));
          if (onInfo) onInfo({ miles: (dist / 1609.34).toFixed(1), minutes: Math.round(dur / 60), stops: stops.length, error: null, legs });
        } else {
          renderer.setMap(null);
          if (onInfo) onInfo({ error: status });
        }
      }
    );
    return () => { cancelled = true; };
  }, [map, routesLib, enabled, warehouse, stops, onInfo]);

  useEffect(() => () => { if (rendererRef.current) rendererRef.current.setMap(null); }, []);
  return null;
};

// Desenha a rota entre dois pontos (endereço simulado -> opção da IA, ou pino A -> pino B)
const SuggestRoute = ({ origin, destination, onInfo, color = '#7c3aed' }) => {
  const map = useMap();
  const routesLib = useMapsLibrary('routes');
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!map || !routesLib) return undefined;
    if (!rendererRef.current) {
      rendererRef.current = new routesLib.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: { strokeColor: color, strokeWeight: 5, strokeOpacity: 0.9 },
      });
    } else {
      rendererRef.current.setOptions({ polylineOptions: { strokeColor: color, strokeWeight: 5, strokeOpacity: 0.9 } });
    }
    const renderer = rendererRef.current;
    if (!origin || !destination) {
      renderer.setMap(null);
      if (onInfo) onInfo(null);
      return undefined;
    }
    renderer.setMap(map);
    const svc = new routesLib.DirectionsService();
    let cancelled = false;
    svc.route(
      { origin, destination, travelMode: 'DRIVING' },
      (res, status) => {
        if (cancelled) return;
        if (status === 'OK' && res.routes && res.routes[0]) {
          renderer.setDirections(res);
          const leg = res.routes[0].legs[0];
          if (onInfo && leg) onInfo({ miles: (leg.distance.value / 1609.34).toFixed(1), minutes: Math.round(leg.duration.value / 60), error: null });
        } else {
          renderer.setMap(null);
          if (onInfo) onInfo({ error: status });
        }
      }
    );
    return () => { cancelled = true; };
  }, [map, routesLib, origin, destination, onInfo, color]);

  useEffect(() => () => { if (rendererRef.current) rendererRef.current.setMap(null); }, []);
  return null;
};

// Desenha uma rota passando por vários pinos na ordem informada (sem otimização)
const MultiStopRoute = ({ stops, onInfo, color = '#f59e0b' }) => {
  const map = useMap();
  const routesLib = useMapsLibrary('routes');
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!map || !routesLib) return undefined;
    if (!rendererRef.current) {
      rendererRef.current = new routesLib.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: { strokeColor: color, strokeWeight: 5, strokeOpacity: 0.9 },
      });
    }
    const renderer = rendererRef.current;
    if (!stops || stops.length < 2) {
      renderer.setMap(null);
      if (onInfo) onInfo(null);
      return undefined;
    }
    renderer.setMap(map);
    const svc = new routesLib.DirectionsService();
    let cancelled = false;
    const origin = { lat: stops[0].lat, lng: stops[0].lng };
    const destination = { lat: stops[stops.length - 1].lat, lng: stops[stops.length - 1].lng };
    const waypoints = stops.slice(1, -1).map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true }));
    svc.route(
      { origin, destination, waypoints, optimizeWaypoints: false, travelMode: 'DRIVING' },
      (res, status) => {
        if (cancelled) return;
        if (status === 'OK' && res.routes && res.routes[0]) {
          renderer.setDirections(res);
          let dist = 0; let dur = 0;
          res.routes[0].legs.forEach((l) => { dist += l.distance.value; dur += l.duration.value; });
          const legs = res.routes[0].legs.map((l) => ({ miles: (l.distance.value / 1609.34).toFixed(1), minutes: Math.round(l.duration.value / 60) }));
          if (onInfo) onInfo({ miles: (dist / 1609.34).toFixed(1), minutes: Math.round(dur / 60), legs, error: null });
        } else {
          renderer.setMap(null);
          if (onInfo) onInfo({ error: status });
        }
      }
    );
    return () => { cancelled = true; };
  }, [map, routesLib, stops, onInfo, color]);

  useEffect(() => () => { if (rendererRef.current) rendererRef.current.setMap(null); }, []);
  return null;
};

// Item arrastável (drag-and-drop) da rota multi-parada
const SortableStop = ({ stop, index, letter, onRemove, pt }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`compare-stop-${index}`}
      className={`flex items-center gap-1.5 text-[11px] text-zinc-700 rounded px-1 py-1 bg-white ${isDragging ? 'shadow-lg ring-1 ring-amber-300' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        data-testid={`compare-drag-${index}`}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-700 cursor-grab active:cursor-grabbing touch-none"
        title={pt ? 'Arraste para reordenar' : 'Drag to reorder'}
        aria-label={pt ? 'Arraste para reordenar' : 'Drag to reorder'}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="shrink-0 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">{letter}</span>
      <span className="font-semibold shrink-0" style={{ color: colorForTeam(stop.team) }}>{stop.team}</span>
      <span className="truncate flex-1">· {stop.customer_name || '—'}{stop.time ? ` · ${stop.time}` : ''}</span>
      <button data-testid={`compare-remove-${index}`} onClick={() => onRemove(stop.id)} className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-red-500" title={pt ? 'Remover' : 'Remove'}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

// Detecta a altura do teclado no celular (iOS/Android) via visualViewport,
// para levantar os bottom-sheets acima do teclado.
const useKeyboardInset = () => {
  const [inset, setInset] = useState(0);
  // App nativo (Capacitor / app da Apple): usa o plugin Keyboard, pois o
  // visualViewport nem sempre reflete o teclado dentro do WebView.
  useEffect(() => {
    let isNative = false;
    try { isNative = Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform(); } catch (e) { isNative = false; }
    if (!isNative) return undefined;
    let showSub, hideSub;
    (async () => {
      try {
        showSub = await Keyboard.addListener('keyboardWillShow', (info) => setInset(Math.round((info && info.keyboardHeight) || 0)));
        hideSub = await Keyboard.addListener('keyboardWillHide', () => setInset(0));
      } catch (e) { /* plugin indisponível */ }
    })();
    return () => { try { showSub && showSub.remove(); hideSub && hideSub.remove(); } catch (e) {} };
  }, []);
  // Navegador (Chrome/Safari mobile): usa visualViewport.
  useEffect(() => {
    let isNative = false;
    try { isNative = Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform(); } catch (e) { isNative = false; }
    if (isNative) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setInset(kb > 80 ? Math.round(kb) : 0); // ignora variações pequenas (barra de URL)
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
    return () => { vv.removeEventListener('resize', onResize); vv.removeEventListener('scroll', onResize); };
  }, []);
  return inset;
};

const ScheduleMapModal = ({ isOpen, onClose, user, language, initialTeams, initialRange }) => {
  const isAdmin = user?.role === 'admin' || user?.role === 'general_manager';
  const pt = language === 'pt-BR';

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(endOfNextMonth());
  const [selectedTeams, setSelectedTeams] = useState([]); // vazio = todas as equipes
  // Pré-seleciona equipes/período quando aberto a partir da comparação (Admin > Comparar Trabalhos)
  useEffect(() => {
    if (isOpen && initialTeams && initialTeams.length) {
      setSelectedTeams([...initialTeams]);
      setCompareStops([]);
      setCompareInfo(null);
    }
    if (isOpen && initialRange && initialRange.from && initialRange.to) {
      setDateFrom(initialRange.from);
      setDateTo(initialRange.to);
    }
  }, [isOpen, initialTeams, initialRange]);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Períodos rápidos do botão Filtro
  const applyQuickRange = (kind) => {
    const n = new Date();
    if (kind === 'today') {
      const t = todayStr(n);
      setDateFrom(t); setDateTo(t);
    } else if (kind === 'week') {
      const start = new Date(n.getFullYear(), n.getMonth(), n.getDate() - n.getDay());
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      setDateFrom(todayStr(start)); setDateTo(todayStr(end));
    } else if (kind === 'month') {
      setDateFrom(todayStr(new Date(n.getFullYear(), n.getMonth(), 1)));
      setDateTo(todayStr(new Date(n.getFullYear(), n.getMonth() + 1, 0)));
    } else if (kind === 'next_month') {
      setDateFrom(todayStr(new Date(n.getFullYear(), n.getMonth() + 1, 1)));
      setDateTo(todayStr(new Date(n.getFullYear(), n.getMonth() + 2, 0)));
    } else {
      setDateFrom(firstOfMonth()); setDateTo(endOfNextMonth());
    }
    setFilterOpen(false);
  };
  const [measureMode, setMeasureMode] = useState(false); // "Medir distância": clicar em pinos monta a rota
  const kbInset = useKeyboardInset();
  const [compareStops, setCompareStops] = useState([]); // paradas da rota selecionada
  const [compareInfo, setCompareInfo] = useState(null);
  // Campo de endereço da "Medir distância": digita e cria um stop na fila
  const [measureAddr, setMeasureAddr] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeErr, setGeocodeErr] = useState(null);
  // Autocomplete de endereços (Google Places, via backend)
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  // Sensor de drag-and-drop (funciona com mouse e toque)
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(0);
  const [selectedJob, setSelectedJob] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addAddress, setAddAddress] = useState('');
  const [addCity, setAddCity] = useState('');
  const [addState, setAddState] = useState('GA');
  const [addZip, setAddZip] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestError, setSuggestError] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null); // opção clicada pela IA
  const [suggestRouteInfo, setSuggestRouteInfo] = useState(null);
  const [mapAuthError, setMapAuthError] = useState(false); // falha de autenticação do Google Maps (chave/referrer)
  // Período da simulação da IA (independente do período do mapa): começa hoje, sem datas passadas
  const [suggestFrom, setSuggestFrom] = useState(todayStr());
  const [suggestTo, setSuggestTo] = useState(monthsAheadEnd(3));

  // Captura falhas de autenticação do Google Maps (chave inválida ou domínio/referrer não permitido)
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => setMapAuthError(true);
    return () => { window.gm_authFailure = prev; };
  }, [isOpen]);

  // Rota do Dia
  const [routeMode, setRouteMode] = useState(false);
  const [routeTeam, setRouteTeam] = useState(isAdmin ? 'PM1' : (user?.username || 'PM1'));
  const [routeDate, setRouteDate] = useState(firstOfMonth());
  const [routeWarehouse, setRouteWarehouse] = useState('Lawrenceville');
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeJobs, setRouteJobs] = useState([]); // trabalhos buscados especificamente para o dia da rota

  const pollRef = useRef(null);
  const routePollRef = useRef(null);

  const fetchMap = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      const { data } = await axios.get(`${API}/schedules/map`, { params });
      setJobs(normalizeJobs(data.jobs));
      setPending(data.pending_geocode || 0);
      // Auto refetch enquanto houver endereços a geocodificar
      if ((data.pending_geocode || 0) > 0) {
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = setTimeout(() => { fetchMap(); }, 2500);
      }
    } catch (e) {
      console.error('Erro ao carregar mapa:', e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!isOpen) return;
    fetchMap();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [isOpen, fetchMap]);

  // Rota do Dia: busca os trabalhos do DIA selecionado direto do backend (independente do período da barra).
  // O endpoint já inclui trabalhos multi-dia que SOBREPÕEM o dia (ex.: começa antes e cobre esse dia).
  useEffect(() => {
    if (!isOpen || !routeMode || !routeDate) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/schedules/map`, { params: { date_from: routeDate, date_to: routeDate } });
        if (cancelled) return;
        setRouteJobs(normalizeJobs(data.jobs));
        if ((data.pending_geocode || 0) > 0) {
          if (routePollRef.current) clearTimeout(routePollRef.current);
          routePollRef.current = setTimeout(load, 2500);
        }
      } catch (e) {
        if (!cancelled) setRouteJobs([]);
      }
    };
    load();
    return () => { cancelled = true; if (routePollRef.current) clearTimeout(routePollRef.current); };
  }, [isOpen, routeMode, routeDate]);

  // Autocomplete: busca previsões (debounce) enquanto digita o endereço em "Medir distância"
  useEffect(() => {
    if (!isOpen || !measureMode) { setPredictions([]); setShowPredictions(false); return undefined; }
    const q = (measureAddr || '').trim();
    if (q.length < 3) { setPredictions([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/maps/autocomplete`, { params: { q } });
        if (cancelled) return;
        const preds = data?.predictions || [];
        setPredictions(preds);
        setShowPredictions(preds.length > 0);
      } catch (e) { if (!cancelled) setPredictions([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [measureAddr, measureMode, isOpen]);

  const runSuggest = async () => {
    if (!addAddress && !addZip && !addCity) {
      setSuggestError(pt ? 'Informe pelo menos o endereço ou CEP.' : 'Enter at least an address or ZIP.');
      return;
    }
    // Nunca pesquisar datas passadas
    const today = todayStr();
    const from = suggestFrom < today ? today : suggestFrom;
    if (suggestTo < from) {
      setSuggestError(pt ? 'A data "Até" deve ser posterior à data "De".' : '"To" date must be after "From" date.');
      return;
    }
    setSuggesting(true);
    setSuggestError(null);
    setSuggestion(null);
    setSelectedSuggestion(null);
    setSuggestRouteInfo(null);
    try {
      const { data } = await axios.post(`${API}/schedules/suggest`, {
        address: addAddress, city: addCity, state: addState, zip_code: addZip,
        date_from: from, date_to: suggestTo,
      });
      setSuggestion(data);
    } catch (e) {
      setSuggestError(e?.response?.data?.detail || (pt ? 'Falha ao simular.' : 'Suggestion failed.'));
    } finally {
      setSuggesting(false);
    }
  };

  // Para PM, o backend já retorna somente a equipe dele (o username pode não ser igual ao nome da equipe,
  // então não filtramos por routeTeam nesse caso). Para admin, filtra pela equipe escolhida.
  const routeJobsForTeam = useMemo(
    () => (isAdmin ? routeJobs.filter((j) => j.team === routeTeam) : routeJobs),
    [isAdmin, routeJobs, routeTeam]
  );

  const routeStops = useMemo(() => {
    if (!routeMode) return [];
    const toMin = (t) => {
      if (!t || !/^\d{1,2}:\d{2}/.test(t)) return 99999; // sem horário vai para o fim
      const [h, m] = t.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    return routeJobsForTeam
      .filter((j) => j.lat && j.lng)
      .map((j) => ({ lat: j.lat, lng: j.lng, customer_name: j.customer_name, address: j.address, city: j.city, zip_code: j.zip_code, time: j.time || '' }))
      .sort((a, b) => toMin(a.time) - toMin(b.time)); // ordem = horário de início (primeiro -> último)
  }, [routeMode, routeJobsForTeam]);

  if (!isOpen) return null;

  const allJobsGeo = jobs.filter((j) => j.lat && j.lng);
  const allTeams = Array.from(new Set(jobs.flatMap((j) => (j.teams && j.teams.length ? j.teams : [j.team])).filter(Boolean))).sort((a, b) => {
    const na = parseInt(String(a).replace('PM', '')) || 999;
    const nb = parseInt(String(b).replace('PM', '')) || 999;
    return na - nb;
  });
  // Filtro por equipes (checkbox): vazio = todas. Multi-equipe: mostra se QUALQUER equipe do trabalho estiver marcada.
  const geocoded = allJobsGeo.filter((j) => selectedTeams.length === 0 || selectedTeams.includes(j.team) || (j.teams || []).some((t) => selectedTeams.includes(t)));
  // Dedup: 1 pino por trabalho (mesma equipe + order/cliente+endereço), mesclando as datas cobertas.
  const pins = (() => {
    const groups = {};
    const orderKeys = [];
    for (const j of geocoded) {
      const idKey = (j.order_number && String(j.order_number).trim()) || `${j.customer_name || ''}|${j.address || ''}`;
      const key = `${j.team}||${idKey}`;
      const jd = (j.dates && j.dates.length) ? j.dates : [j.date];
      if (!groups[key]) {
        groups[key] = { ...j, dates: [...jd] };
        orderKeys.push(key);
      } else {
        const g = groups[key];
        g.dates = Array.from(new Set([...(g.dates || []), ...jd])).sort();
        if ((j.duration_days || 1) > (g.duration_days || 1)) g.duration_days = j.duration_days;
      }
    }
    return orderKeys.map((k) => groups[k]);
  })();
  const teamsPresent = Array.from(new Set(pins.flatMap((j) => (j.teams && j.teams.length ? j.teams : [j.team])))).sort();
  const fmtDate = (d) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: '2-digit', month: 'short' }); }
    catch (_) { return d; }
  };
  // Formata lista de datas compacta: "22, 23, 24/07"
  const fmtDates = (dates) => {
    if (!dates || dates.length === 0) return '';
    if (dates.length === 1) return fmtDate(dates[0]);
    const byMonth = {};
    const order = [];
    [...dates].sort().forEach((d) => {
      const parts = String(d).split('-');
      if (parts.length < 3) return;
      const mk = `${parts[0]}-${parts[1]}`;
      if (!byMonth[mk]) { byMonth[mk] = []; order.push(mk); }
      byMonth[mk].push(parseInt(parts[2], 10));
    });
    return order.map((mk) => `${byMonth[mk].join(', ')}/${mk.split('-')[1]}`).join(' · ');
  };
  const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '📍');

  // Alterna uma equipe no filtro de checkbox
  const toggleTeam = (t) => {
    setCompareStops([]); setCompareInfo(null);
    setSelectedTeams((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  // Clique num pino: se "Medir distância" estiver ligado, adiciona à rota; senão, só mostra as informações
  const handlePinClick = (j) => {
    if (routeMode || showAdd) { setSelectedJob(j); return; }
    if (measureMode) {
      setCompareStops((prev) => (prev.some((s) => s.id === j.id) ? prev : [...prev, j]));
    } else {
      setSelectedJob(j);
    }
  };
  const clearCompare = () => { setCompareStops([]); setCompareInfo(null); };
  const removeCompareStop = (id) => setCompareStops((prev) => prev.filter((s) => s.id !== id));
  // Medir distância: adiciona um ponto livre clicado em qualquer lugar do mapa
  const addMapPoint = (lat, lng) => {
    setCompareStops((prev) => {
      const n = prev.filter((s) => s.kind === 'point').length + 1;
      return [...prev, { id: `pt-${Date.now()}`, lat, lng, team: '📍', customer_name: `${pt ? 'Ponto' : 'Point'} ${n}`, kind: 'point' }];
    });
  };
  // Medir distância: adiciona um warehouse como parada
  const addWarehouseStop = (name) => {
    const w = WAREHOUSES[name];
    if (!w) return;
    setCompareStops((prev) => (prev.some((s) => s.id === `wh-${name}`) ? prev : [...prev, { id: `wh-${name}`, lat: w.lat, lng: w.lng, team: '🏭', customer_name: `Warehouse ${name}`, kind: 'warehouse' }]));
  };
  // Medir distância: digita um endereço, geocodifica (backend) e cria um stop na fila
  const addAddressStop = async () => {
    const q = (measureAddr || '').trim();
    if (!q) return;
    setGeocoding(true);
    setGeocodeErr(null);
    try {
      const { data: r } = await axios.post(`${API}/maps/geocode`, { q });
      if (r && r.found && typeof r.lat === 'number') {
        setCompareStops((prev) => {
          const n = prev.filter((s) => s.kind === 'address').length + 1;
          return [...prev, { id: `addr-${Date.now()}`, lat: r.lat, lng: r.lng, team: '📮', customer_name: r.formatted || `${pt ? 'Endereço' : 'Address'} ${n}`, kind: 'address' }];
        });
        setMeasureAddr('');
        setPredictions([]);
        setShowPredictions(false);
      } else {
        setGeocodeErr(pt ? 'Endereço não encontrado. Tente ser mais específico.' : 'Address not found. Be more specific.');
      }
    } catch (e) {
      setGeocodeErr(pt ? 'Falha ao localizar o endereço.' : 'Failed to locate the address.');
    } finally {
      setGeocoding(false);
    }
  };
  // Medir distância: seleciona uma sugestão do autocomplete e cria o stop (geocode via backend)
  const selectPrediction = async (pred) => {
    setShowPredictions(false);
    setPredictions([]);
    setGeocoding(true);
    setGeocodeErr(null);
    try {
      const { data: r } = await axios.post(`${API}/maps/geocode`, { q: pred.description });
      if (r && r.found && typeof r.lat === 'number') {
        setCompareStops((prev) => {
          const n = prev.filter((s) => s.kind === 'address').length + 1;
          return [...prev, { id: `addr-${Date.now()}`, lat: r.lat, lng: r.lng, team: '📮', customer_name: pred.description || r.formatted || `${pt ? 'Endereço' : 'Address'} ${n}`, kind: 'address' }];
        });
        setMeasureAddr('');
      } else {
        setGeocodeErr(pt ? 'Não consegui localizar esse endereço.' : 'Could not locate that address.');
      }
    } catch (e) {
      setGeocodeErr(pt ? 'Falha ao localizar o endereço.' : 'Failed to locate the address.');
    } finally {
      setGeocoding(false);
    }
  };
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setCompareStops((prev) => {
      const oldI = prev.findIndex((s) => s.id === active.id);
      const newI = prev.findIndex((s) => s.id === over.id);
      if (oldI < 0 || newI < 0) return prev;
      return arrayMove(prev, oldI, newI);
    });
  };
  const toggleMeasure = () => {
    setMeasureMode((v) => {
      if (v) { setCompareStops([]); setCompareInfo(null); } // desligou -> limpa
      return !v;
    });
  };

  const candidatePt = suggestion?.candidate;

  // Enquadramentos independentes por gatilho (não reenquadra ao clicar num pin ou fechar painel):
  // 1) Trabalhos: só quando o conjunto de jobs geocodificados muda (carregar/filtrar/período)
  const jobsFitPoints = pins.map((j) => ({ lat: j.lat, lng: j.lng }));
  const jobsTrigger = 'jobs:' + jobsFitPoints.map((p) => `${p.lat},${p.lng}`).join('|');
  // 2) Endereço simulado: foca ao rodar uma nova simulação
  const candidateTrigger = candidatePt ? `cand:${candidatePt.lat},${candidatePt.lng}` : null;
  // 3) Opção escolhida pela IA: enquadra endereço + trabalho mais próximo ao clicar na opção
  const optionTrigger = selectedSuggestion?.nearest_lat ? `opt:${selectedSuggestion.team}-${selectedSuggestion.date}` : null;

  const routeWarehouseObj = WAREHOUSES[routeWarehouse];

  // Abre a rota do dia no app do Google Maps (ordem = horário de início)
  const openRouteInGoogleMaps = () => {
    if (!routeWarehouseObj || routeStops.length === 0) return;
    const origin = `${routeWarehouseObj.lat},${routeWarehouseObj.lng}`;
    const dest = routeStops[routeStops.length - 1];
    const destination = `${dest.lat},${dest.lng}`;
    const mids = routeStops.slice(0, -1).map((s) => `${s.lat},${s.lng}`);
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (mids.length) url += `&waypoints=${encodeURIComponent(mids.join('|'))}`;
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    window.open(url, isNative ? '_system' : '_blank');
  };

  // Abre a rota do dia no Apple Maps (ordem = horário de início)
  const openRouteInAppleMaps = () => {
    if (!routeWarehouseObj || routeStops.length === 0) return;
    const saddr = `${routeWarehouseObj.lat},${routeWarehouseObj.lng}`;
    const daddr = routeStops.map((s) => `${s.lat},${s.lng}`).join('+to:');
    const url = `https://maps.apple.com/?saddr=${saddr}&daddr=${daddr}&dirflg=d`;
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    window.open(url, isNative ? '_system' : '_blank');
  };

  return (
    <div data-testid="schedule-map-modal" style={{ position: 'fixed', inset: 0, zIndex: 99998, display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-zinc-200 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-lg">🗺️</span>
          <span className="font-semibold text-sm sm:text-base text-zinc-900">{pt ? 'Mapa de Trabalhos' : 'Jobs Map'}</span>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
          {pending > 0 && <span className="text-[11px] text-amber-600">{pt ? `geocodificando ${pending}...` : `geocoding ${pending}...`}</span>}
        </div>
        <button data-testid="map-close-btn" onClick={onClose} aria-label="Fechar" className="w-8 h-8 flex items-center justify-center rounded hover:bg-zinc-100">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 shrink-0">
        <div className="relative">
          <button
            type="button"
            data-testid="map-filter-btn"
            onClick={() => setFilterOpen((v) => !v)}
            className={`text-xs border rounded px-2 py-1 flex items-center gap-1 ${filterOpen ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-zinc-300 hover:bg-zinc-50'}`}
          >
            🔍 {pt ? 'Filtro' : 'Filter'}
            <span className="text-[10px] text-zinc-500">{fmtRangeShort(dateFrom, dateTo)}</span>
            <span className="text-zinc-400">▾</span>
          </button>
          {filterOpen && (
            <>
              <div className="fixed inset-0 z-[65]" onClick={() => setFilterOpen(false)} />
              <div data-testid="map-filter-dropdown" className="absolute left-0 top-full mt-1 z-[70] bg-white border border-zinc-200 rounded-lg shadow-xl p-3 w-64 space-y-2">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase">{pt ? 'Período rápido' : 'Quick range'}</p>
                <div className="flex flex-wrap gap-1">
                  <button type="button" data-testid="map-filter-today" onClick={() => applyQuickRange('today')} className="text-[11px] border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 hover:border-indigo-300">{pt ? 'Hoje' : 'Today'}</button>
                  <button type="button" data-testid="map-filter-week" onClick={() => applyQuickRange('week')} className="text-[11px] border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 hover:border-indigo-300">{pt ? 'Esta semana' : 'This week'}</button>
                  <button type="button" data-testid="map-filter-month" onClick={() => applyQuickRange('month')} className="text-[11px] border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 hover:border-indigo-300">{pt ? 'Este mês' : 'This month'}</button>
                  <button type="button" data-testid="map-filter-next-month" onClick={() => applyQuickRange('next_month')} className="text-[11px] border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 hover:border-indigo-300">{pt ? 'Mês que vem' : 'Next month'}</button>
                  <button type="button" data-testid="map-filter-default" onClick={() => applyQuickRange('default')} className="text-[11px] border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 hover:border-indigo-300">{pt ? 'Padrão (2 meses)' : 'Default (2 months)'}</button>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-zinc-400 uppercase mb-1">{pt ? 'Dia específico' : 'Specific day'}</p>
                  <input
                    data-testid="map-filter-day"
                    type="date"
                    value={dateFrom === dateTo ? dateFrom : ''}
                    onChange={(e) => { if (e.target.value) { setDateFrom(e.target.value); setDateTo(e.target.value); } }}
                    className="w-full text-xs border border-zinc-300 rounded px-2 py-1"
                  />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-zinc-400 uppercase mb-1">{pt ? 'Intervalo (De / Até)' : 'Range (From / To)'}</p>
                  <div className="flex items-center gap-1">
                    <input data-testid="map-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="flex-1 min-w-0 text-xs border border-zinc-300 rounded px-1.5 py-1" />
                    <span className="text-zinc-400 text-xs">→</span>
                    <input data-testid="map-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="flex-1 min-w-0 text-xs border border-zinc-300 rounded px-1.5 py-1" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {isAdmin && (
          <div className="relative">
            <button
              type="button"
              data-testid="map-team-filter-btn"
              onClick={() => setTeamDropdownOpen((v) => !v)}
              className="text-xs border border-zinc-300 rounded px-2 py-1 bg-white hover:bg-zinc-50 flex items-center gap-1"
            >
              👥 {selectedTeams.length === 0 ? (pt ? 'Todas equipes' : 'All teams') : selectedTeams.map(teamLabel).join(', ')}
              <span className="text-zinc-400">▾</span>
            </button>
            {teamDropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setTeamDropdownOpen(false)} />
                <div data-testid="map-team-dropdown" className="absolute left-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-lg shadow-xl p-2 w-44 max-h-64 overflow-auto">
                  <label className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-zinc-50 cursor-pointer font-semibold">
                    <input type="checkbox" data-testid="team-checkbox-all" checked={selectedTeams.length === 0} onChange={() => { setCompareStops([]); setCompareInfo(null); setSelectedTeams([]); }} />
                    {pt ? 'Todas equipes' : 'All teams'}
                  </label>
                  <div className="h-px bg-zinc-100 my-1" />
                  {[...(allTeams.length ? allTeams : ['PM1', 'PM2', 'PM3', 'PM4', 'PM5', 'PM6', 'PM7', 'PM8', 'PM9', 'PM10']).filter((t) => t !== MEASURE_TEAM), MEASURE_TEAM].map((t) => (
                    <label key={t} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-zinc-50 cursor-pointer">
                      <input type="checkbox" data-testid={`team-checkbox-${t}`} checked={selectedTeams.includes(t)} onChange={() => toggleTeam(t)} />
                      <span style={{ width: 9, height: 9, borderRadius: 999, background: colorForTeam(t), display: 'inline-block' }} />
                      {teamLabel(t)}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <label data-testid="map-measure-toggle" className={`flex items-center gap-1.5 text-xs border rounded px-2 py-1 cursor-pointer select-none ${measureMode ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-50'}`} title={pt ? 'Toque em pinos, warehouses ou em qualquer lugar do mapa para montar uma rota e medir a distância. Arraste para reordenar.' : 'Tap pins, warehouses or anywhere on the map to build a route and measure. Drag to reorder.'}>
          <input type="checkbox" data-testid="map-measure-checkbox" checked={measureMode} onChange={toggleMeasure} className="accent-amber-500" />
          📏 {pt ? 'Medir distância' : 'Measure distance'}
        </label>
        <Button data-testid="map-refresh-btn" variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={fetchMap}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <span className="text-[11px] text-zinc-500 ml-auto">{pins.length} {pt ? 'no mapa' : 'on map'}</span>
        <Button data-testid="map-route-btn" size="sm" variant={routeMode ? 'default' : 'outline'} className={`h-7 px-2 text-xs ${routeMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`} onClick={() => { clearCompare(); setShowAdd(false); setRouteMode((v) => !v); }}>
          <Route className="h-3.5 w-3.5 mr-1" />{pt ? 'Rota do Dia' : 'Day Route'}
        </Button>
        <Button data-testid="map-add-address-btn" size="sm" className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { clearCompare(); setRouteMode(false); setShowAdd((v) => !v); }}>
          <MapPin className="h-3.5 w-3.5 mr-1" />{pt ? 'Add Endereço' : 'Add Address'}
        </Button>
      </div>

      {/* Body: mapa + painel */}
      <div className="relative flex-1 min-h-0">
        {!GMAPS_KEY ? (
          <div className="h-full flex items-center justify-center text-center p-6">
            <div className="bg-white rounded-lg p-6 border border-zinc-200 max-w-md">
              <p className="text-sm text-red-600 font-semibold">{pt ? 'Chave do Google Maps não configurada' : 'Google Maps API key not configured'}</p>
              <p className="text-xs text-zinc-500 mt-2">REACT_APP_GOOGLE_MAPS_API_KEY</p>
            </div>
          </div>
        ) : (
          <APIProvider apiKey={GMAPS_KEY}>
            {mapAuthError && (
              <div data-testid="map-auth-error" className="absolute inset-x-0 top-0 z-[60] bg-red-600 text-white text-xs px-3 py-2 shadow-lg">
                <p className="font-semibold">{pt ? '⚠️ Google Maps recusou a chave neste app' : '⚠️ Google Maps rejected the key in this app'}</p>
                <p className="mt-0.5 opacity-90">
                  {pt
                    ? 'No Google Cloud → Credentials → sua chave → "Website restrictions", adicione: capacitor://localhost/* , https://localhost/* , https://*.emergent.host/*'
                    : 'In Google Cloud → Credentials → your key → "Website restrictions", add: capacitor://localhost/* , https://localhost/* , https://*.emergent.host/*'}
                </p>
              </div>
            )}
            <Map
              defaultCenter={REGION_CENTER}
              defaultZoom={REGION_ZOOM}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={false}
              onClick={(ev) => {
                if (measureMode && !routeMode && !showAdd && ev?.detail?.latLng) {
                  addMapPoint(ev.detail.latLng.lat, ev.detail.latLng.lng);
                }
              }}
              onCameraChanged={(ev) => { if (ev?.detail) window.__mapZoom = ev.detail.zoom; }}
              style={{ width: '100%', height: '100%' }}
            >
              <FitBounds points={jobsFitPoints} triggerKey={jobsTrigger} />
              {candidatePt && <FitBounds points={[{ lat: candidatePt.lat, lng: candidatePt.lng }]} triggerKey={candidateTrigger} zoom={12} />}
              {optionTrigger && candidatePt && (
                <FitBounds
                  points={[{ lat: candidatePt.lat, lng: candidatePt.lng }, { lat: selectedSuggestion.nearest_lat, lng: selectedSuggestion.nearest_lng }]}
                  triggerKey={optionTrigger}
                />
              )}
              <DayRoute enabled={routeMode} warehouse={routeWarehouseObj} stops={routeStops} onInfo={setRouteInfo} />
              <SuggestRoute
                origin={!routeMode && showAdd && candidatePt && selectedSuggestion?.nearest_lat ? { lat: candidatePt.lat, lng: candidatePt.lng } : null}
                destination={!routeMode && showAdd && candidatePt && selectedSuggestion?.nearest_lat ? { lat: selectedSuggestion.nearest_lat, lng: selectedSuggestion.nearest_lng } : null}
                onInfo={setSuggestRouteInfo}
              />
              {/* Rota selecionada por cliques (multi-parada) */}
              <MultiStopRoute
                stops={!routeMode && !showAdd && measureMode && compareStops.length >= 2 ? compareStops : null}
                onInfo={setCompareInfo}
                color="#f59e0b"
              />
              {pins.map((j) => {
                const jt = j.teams && j.teams.length > 1 ? j.teams : null;
                const label = jt ? jt.join('+') : j.team;
                return (
                  <Marker
                    key={j.id}
                    position={{ lat: j.lat, lng: j.lng }}
                    icon={teamTagIcon(label, colorForTeam(j.team), fmtPinDate(j), !!jt)}
                    title={`${j.customer_name || ''} (${jt ? jt.join(', ') : j.team})`}
                    onClick={() => handlePinClick(j)}
                  />
                );
              })}
              {/* Realce dos pinos selecionados para a rota (A, B, C...) */}
              {!routeMode && !showAdd && measureMode && compareStops.map((s, i) => (
                <Marker
                  key={`cmp-${s.id}`}
                  position={{ lat: s.lat, lng: s.lng }}
                  icon={compareBadgeIcon(String.fromCharCode(65 + i))}
                  zIndex={10000}
                  title={`${String.fromCharCode(65 + i)}: ${s.customer_name || ''} (${s.team})`}
                />
              ))}
              {candidatePt && (
                <Marker position={{ lat: candidatePt.lat, lng: candidatePt.lng }} icon={candidateIcon()} title={pt ? 'Endereço simulado' : 'Simulated address'} zIndex={9999} />
              )}
              {!routeMode && showAdd && selectedSuggestion?.nearest_lat && (
                <Marker
                  position={{ lat: selectedSuggestion.nearest_lat, lng: selectedSuggestion.nearest_lng }}
                  icon={teamTagIcon(selectedSuggestion.team, colorForTeam(selectedSuggestion.team))}
                  title={`${selectedSuggestion.nearest_customer || ''} (${selectedSuggestion.team})`}
                  zIndex={9998}
                />
              )}
              {selectedJob && selectedJob.lat && (
                <InfoWindow position={{ lat: selectedJob.lat, lng: selectedJob.lng }} onCloseClick={() => setSelectedJob(null)}>
                  <div style={{ maxWidth: 220, fontSize: 12, lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{selectedJob.customer_name || '—'}</div>
                    {(selectedJob.teams && selectedJob.teams.length > 1) ? (
                      <div data-testid="infowindow-multiteam" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        <span style={{ display: 'inline-block', background: '#f59e0b', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>👥 {pt ? 'Multi-equipe' : 'Multi-team'}</span>
                        {selectedJob.teams.map((t) => (
                          <span key={t} style={{ display: 'inline-block', background: colorForTeam(t), color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{t}</span>
                        ))}
                        <span style={{ fontSize: 10, color: '#64748b' }}>{selectedJob.job_type}</span>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'inline-block', background: colorForTeam(selectedJob.team), color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{teamLabel(selectedJob.team)}</div>
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#64748b' }}>{selectedJob.job_type}</span>
                        {selectedJob.job_type === 'MEASURE' && selectedJob.measured_done && (
                          <span data-testid="infowindow-measured-done" style={{ marginLeft: 6, display: 'inline-block', background: '#059669', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>✓ {pt ? 'Medido' : 'Measured'}</span>
                        )}
                      </>
                    )}
                    <div style={{ marginTop: 4, color: '#334155' }}>{selectedJob.address || ''}{selectedJob.city ? `, ${selectedJob.city}` : ''} {selectedJob.zip_code || ''}</div>
                    <div style={{ marginTop: 2, color: '#334155' }}>📅 {(selectedJob.dates && selectedJob.dates.length > 1) ? fmtDates(selectedJob.dates) : fmtDate(selectedJob.date)}{((selectedJob.dates && selectedJob.dates.length > 1) || (selectedJob.duration_days || 1) > 1) ? ` · ${(selectedJob.dates && selectedJob.dates.length) || selectedJob.duration_days} ${pt ? 'dias' : 'days'}` : ''}</div>
                    {(() => {
                      const w = selectedJob.windows;
                      const d = selectedJob.doors;
                      const parts = [];
                      if (typeof w === 'number' && w > 0) parts.push(`🪟 ${w} ${pt ? (w === 1 ? 'janela' : 'janelas') : (w === 1 ? 'window' : 'windows')}`);
                      if (typeof d === 'number' && d > 0) parts.push(`🚪 ${d} ${pt ? (d === 1 ? 'porta' : 'portas') : (d === 1 ? 'door' : 'doors')}`);
                      return parts.length > 0 ? <div style={{ marginTop: 3, color: '#0f172a', fontWeight: 600 }}>{parts.join(' · ')}</div> : null;
                    })()}
                    {selectedJob.order_number && <div style={{ color: '#64748b', fontSize: 11 }}>#{selectedJob.order_number}</div>}
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        )}

        {/* Card da rota selecionada por cliques/toques (multi-parada) */}
        {!routeMode && !showAdd && measureMode && (
          <div data-testid="map-compare-card" className="absolute top-2 left-1/2 -translate-x-1/2 z-30 bg-white rounded-lg shadow-xl border border-amber-200 px-3 py-2 w-[92%] sm:w-80 max-w-[94vw] max-h-[70%] flex flex-col">
            <div className="flex items-center justify-between mb-1 shrink-0">
              <span className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Navigation className="h-3.5 w-3.5" />📏 {pt ? 'Medir distância' : 'Measure distance'}</span>
              {compareStops.length > 0 && (
                <button data-testid="compare-clear-btn" onClick={clearCompare} className="text-[11px] text-zinc-500 hover:text-zinc-800">{pt ? 'Limpar' : 'Clear'}</button>
              )}
            </div>
            {/* Adicionar warehouse rapidamente */}
            <div className="flex flex-wrap gap-1 mb-1.5 shrink-0" data-testid="measure-warehouses">
              <span className="text-[10px] text-zinc-400 self-center">{pt ? 'Add:' : 'Add:'}</span>
              {Object.keys(WAREHOUSES).map((w) => (
                <button
                  key={w}
                  data-testid={`measure-add-wh-${w}`}
                  onClick={() => addWarehouseStop(w)}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-zinc-300 bg-zinc-50 hover:bg-amber-50 text-zinc-700 flex items-center gap-0.5"
                >
                  🏭 {w}
                </button>
              ))}
            </div>
            {/* Digitar um endereço e criar uma parada na fila */}
            <div className="mb-1.5 shrink-0 relative" data-testid="measure-address-box">
              <div className="flex items-center gap-1">
                <input
                  data-testid="measure-address-input"
                  value={measureAddr}
                  onChange={(e) => { setMeasureAddr(e.target.value); if (geocodeErr) setGeocodeErr(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showPredictions && predictions.length > 0) { selectPrediction(predictions[0]); }
                      else { addAddressStop(); }
                    } else if (e.key === 'Escape') { setShowPredictions(false); }
                  }}
                  onFocus={(e) => { if (predictions.length > 0) setShowPredictions(true); const t = e.target; setTimeout(() => { try { t.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {} }, 350); }}
                  placeholder={pt ? 'Digite um endereço para adicionar…' : 'Type an address to add…'}
                  className="flex-1 min-w-0 text-xs border border-zinc-300 rounded px-2 py-1.5"
                  style={{ fontSize: 16 }}
                  autoComplete="off"
                />
                <button
                  data-testid="measure-address-add-btn"
                  onClick={addAddressStop}
                  disabled={geocoding || !measureAddr.trim()}
                  className="shrink-0 h-8 px-2.5 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1"
                  title={pt ? 'Adicionar endereço à fila' : 'Add address to queue'}
                >
                  {geocoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}
                  {pt ? 'Add' : 'Add'}
                </button>
              </div>
              {/* Sugestões de endereço (autocomplete) */}
              {showPredictions && predictions.length > 0 && (
                <ul data-testid="measure-address-suggestions" className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-zinc-200 rounded-lg shadow-xl max-h-48 overflow-auto py-1">
                  {predictions.map((p, i) => (
                    <li key={p.place_id || i}>
                      <button
                        type="button"
                        data-testid={`measure-address-suggestion-${i}`}
                        onMouseDown={(e) => { e.preventDefault(); selectPrediction(p); }}
                        className="w-full text-left flex items-start gap-1.5 px-2 py-1.5 text-[11px] text-zinc-700 hover:bg-amber-50"
                      >
                        <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                        <span className="leading-snug">{p.description}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {geocodeErr && <p className="text-[10px] text-red-600 mt-0.5" data-testid="measure-address-error">{geocodeErr}</p>}
            </div>
            {compareStops.length > 0 && (
              <div className="space-y-0.5 overflow-auto">
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={compareStops.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    {compareStops.map((s, i) => (
                      <SortableStop key={s.id} stop={s} index={i} letter={String.fromCharCode(65 + i)} onRemove={removeCompareStop} pt={pt} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
            <div className="mt-1.5 pt-1.5 border-t border-zinc-100 text-xs shrink-0">
              {compareStops.length === 0 ? (
                <span className="text-zinc-500">{pt ? 'Toque num pino, num warehouse, digite um endereço acima, ou toque em qualquer lugar do mapa para medir.' : 'Tap a pin, a warehouse, type an address above, or tap anywhere on the map to measure.'}</span>
              ) : compareStops.length < 2 ? (
                <span className="text-zinc-500">{pt ? 'Adicione mais um ponto (pino, warehouse, endereço ou toque no mapa). Arraste para reordenar.' : 'Add one more point (pin, warehouse, address or tap the map). Drag to reorder.'}</span>
              ) : compareInfo?.error ? (
                <span className="text-red-600">{compareInfo.error === 'REQUEST_DENIED'
                  ? (pt ? '⚠️ Habilite a Directions API.' : 'Enable Directions API.')
                  : `${pt ? 'Erro' : 'Error'}: ${compareInfo.error}`}</span>
              ) : compareInfo ? (
                <div className="space-y-1">
                  <span className="font-semibold text-amber-800 block">🚗 {compareInfo.miles} mi · ⏱️ ~{fmtDuration(compareInfo.minutes)} · {compareStops.length} {pt ? 'paradas' : 'stops'}</span>
                  {(compareInfo.legs || []).length > 0 && (
                    <div className="space-y-0.5" data-testid="compare-legs">
                      {compareInfo.legs.map((leg, i) => (
                        <div key={`clg-${i}`} data-testid={`compare-leg-${i}`} className="text-[11px] text-zinc-600 flex items-center gap-1">
                          <span className="font-semibold text-amber-700 shrink-0">{String.fromCharCode(65 + i)} → {String.fromCharCode(66 + i)}</span>
                          <span>· {leg.miles} mi · ~{fmtDuration(leg.minutes)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <span className="flex items-center gap-1 text-amber-600"><Loader2 className="h-3 w-3 animate-spin" />{pt ? 'Calculando rota...' : 'Calculating...'}</span>
              )}
            </div>
          </div>
        )}

        {/* Legenda */}
        {teamsPresent.length > 0 && (
          <div className="absolute bottom-2 left-2 bg-white/95 rounded-lg shadow px-2 py-1.5 flex flex-wrap gap-x-3 gap-y-1 max-w-[70%]" data-testid="map-legend">
            {teamsPresent.map((t) => (
              <span key={t} className="flex items-center gap-1 text-[11px] text-zinc-700">
                <span style={{ width: 10, height: 10, borderRadius: 999, background: colorForTeam(t), display: 'inline-block' }} />{teamLabel(t)}
              </span>
            ))}
          </div>
        )}

        {/* Painel Rota do Dia — bottom sheet no mobile, flutuante à direita no desktop */}
        {routeMode && (
          <div data-testid="map-route-panel" style={kbInset ? { bottom: kbInset } : undefined} className="absolute inset-x-0 bottom-0 top-auto w-full max-h-[52%] rounded-t-2xl sm:inset-x-auto sm:top-2 sm:right-2 sm:bottom-auto sm:w-80 sm:max-h-[calc(100%-1rem)] sm:rounded-lg flex flex-col bg-white shadow-2xl border border-zinc-200 z-30 transition-[bottom] duration-150">
            <div className="sm:hidden flex justify-center pt-1.5"><span className="w-10 h-1 rounded-full bg-zinc-300" /></div>
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 shrink-0">
              <span className="font-semibold text-sm flex items-center gap-1"><Route className="h-4 w-4 text-emerald-600" />{pt ? 'Rota do Dia' : 'Day Route'}</span>
              <button onClick={() => setRouteMode(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 space-y-2 overflow-auto">
              <div className="flex items-center gap-1">
                <Warehouse className="h-4 w-4 text-zinc-500 shrink-0" />
                <select data-testid="route-warehouse" value={routeWarehouse} onChange={(e) => setRouteWarehouse(e.target.value)} className="flex-1 text-sm border border-zinc-300 rounded px-2 py-1.5">
                  {Object.keys(WAREHOUSES).map((w) => <option key={w} value={w}>Warehouse {w}</option>)}
                </select>
              </div>
              {isAdmin && (
                <select data-testid="route-team" value={routeTeam} onChange={(e) => setRouteTeam(e.target.value)} className="w-full text-sm border border-zinc-300 rounded px-2 py-1.5">
                  {['PM1', 'PM2', 'PM3', 'PM4', 'PM5', 'PM6', 'PM7', 'PM8', 'PM9', 'PM10'].map((t) => <option key={t} value={t}>{t}</option>)}
                  <option value={MEASURE_TEAM} data-testid="route-team-measure">📏 {pt ? 'Medições (Measure)' : 'Measurements (Measure)'}</option>
                </select>
              )}
              <input data-testid="route-date" type="date" value={routeDate} onChange={(e) => setRouteDate(e.target.value)} className="w-full text-sm border border-zinc-300 rounded px-2 py-1.5" />
              <div className="text-xs" data-testid="route-info">
                {routeStops.length === 0 ? (
                  routeJobsForTeam.length > 0 ? (
                    <p className="text-amber-600" data-testid="route-geocoding-note">{pt ? '📍 Há trabalho(s) nesse dia, mas o endereço ainda não foi localizado no mapa. Aguarde alguns segundos ou toque em atualizar 🔄.' : 'There are jobs on this day, but their address is not on the map yet. Wait a few seconds or tap refresh 🔄.'}</p>
                  ) : (
                    <p className="text-zinc-500">{routeTeam === MEASURE_TEAM ? (pt ? 'Nenhuma medição nesse dia.' : 'No measurements on this day.') : (pt ? 'Nenhum trabalho dessa equipe nesse dia.' : 'No jobs for this team on this day.')}</p>
                  )
                ) : routeInfo?.error ? (
                  <p className="text-red-600">{routeInfo.error === 'REQUEST_DENIED'
                    ? (pt ? '⚠️ Habilite a "Directions API" no Google Cloud e adicione-a à chave do frontend.' : 'Enable Directions API for the frontend key.')
                    : `${pt ? 'Erro' : 'Error'}: ${routeInfo.error}`}</p>
                ) : routeInfo ? (
                  <div className="space-y-2">
                    <div className="bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-900">
                      <div>🚚 {routeInfo.stops} {pt ? 'paradas' : 'stops'}</div>
                      <div>📏 {routeInfo.miles} mi · ⏱️ ~{fmtDuration(routeInfo.minutes)}</div>
                      <div className="text-[11px] text-emerald-700 mt-1">{pt ? 'Ordem pelo horário de início (1º → último).' : 'Ordered by start time (first → last).'}</div>
                    </div>
                    {/* Início no warehouse */}
                    <div className="flex items-start gap-2 rounded p-2 bg-zinc-50 border border-zinc-100" data-testid="route-leg-warehouse">
                      <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-zinc-800 text-white text-[11px] font-bold flex items-center justify-center">
                        <Warehouse className="w-3.5 h-3.5" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-zinc-800">Warehouse {routeWarehouse}</div>
                        <div className="text-[11px] text-zinc-500">{pt ? 'Ponto de partida' : 'Start point'}</div>
                      </div>
                    </div>
                    {/* Paradas na ordem otimizada: A, B, C... */}
                    {(routeInfo.legs || []).map((leg, i) => {
                      const letter = String.fromCharCode(65 + i);
                      const s = leg.stop || {};
                      const addr = [s.address, s.city, s.zip_code].filter(Boolean).join(', ');
                      const prev = i === 0 ? (pt ? 'Warehouse' : 'Warehouse') : String.fromCharCode(65 + i - 1);
                      return (
                        <div key={`leg-${i}`} className="flex items-start gap-2 rounded p-2 bg-white border border-zinc-100" data-testid={`route-leg-${letter}`}>
                          <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center" style={{ background: colorForTeam(routeTeam) }}>
                            {letter}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              {s.time && <span className="shrink-0 text-[10px] font-bold bg-zinc-800 text-white rounded px-1.5 py-0.5">{s.time}</span>}
                              <span className="font-semibold text-zinc-800 truncate">{s.customer_name || (pt ? 'Cliente' : 'Customer')}</span>
                            </div>
                            {addr && <div className="text-[11px] text-zinc-500 leading-snug">{addr}</div>}
                            <div className="text-[11px] text-emerald-700 mt-0.5">
                              📏 {prev} → {letter}: {leg.distance_mi} mi · ⏱️ ~{fmtDuration(leg.minutes)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Button
                        data-testid="route-open-gmaps-btn"
                        onClick={openRouteInGoogleMaps}
                        className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2"
                      >
                        <Navigation className="h-4 w-4 mr-1" />
                        Google Maps
                      </Button>
                      <Button
                        data-testid="route-open-applemaps-btn"
                        onClick={openRouteInAppleMaps}
                        className="h-9 text-xs bg-zinc-800 hover:bg-zinc-900 text-white px-2"
                      >
                        <Navigation className="h-4 w-4 mr-1" />
                        Apple Maps
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{pt ? 'Calculando rota...' : 'Calculating...'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Painel Add Endereço — bottom sheet no mobile, flutuante à esquerda no desktop */}
        {showAdd && (
          <div data-testid="map-suggest-panel" style={kbInset ? { bottom: kbInset } : undefined} className="absolute inset-x-0 bottom-0 top-auto w-full max-h-[60%] rounded-t-2xl sm:inset-x-auto sm:top-2 sm:left-2 sm:bottom-auto sm:w-96 sm:max-h-[calc(100%-1rem)] sm:rounded-lg flex flex-col overflow-hidden bg-white shadow-2xl border border-zinc-200 z-30 transition-[bottom] duration-150">
            <div className="sm:hidden flex justify-center pt-1.5"><span className="w-10 h-1 rounded-full bg-zinc-300" /></div>
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 shrink-0">
              <span className="font-semibold text-sm flex items-center gap-1"><Sparkles className="h-4 w-4 text-indigo-600" />{pt ? 'Simular melhor dia/equipe' : 'Simulate best day/team'}</span>
              <button onClick={() => setShowAdd(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 space-y-2 overflow-auto">
              <input data-testid="suggest-address" value={addAddress} onChange={(e) => setAddAddress(e.target.value)} onFocus={(e) => { const t = e.target; setTimeout(() => { try { t.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {} }, 350); }} placeholder={pt ? 'Endereço (rua e número)' : 'Street address'} className="w-full text-sm border border-zinc-300 rounded px-2 py-1.5" />
              <div className="flex gap-2">
                <input data-testid="suggest-city" value={addCity} onChange={(e) => setAddCity(e.target.value)} placeholder={pt ? 'Cidade' : 'City'} className="flex-1 text-sm border border-zinc-300 rounded px-2 py-1.5" />
                <select data-testid="suggest-state" value={addState} onChange={(e) => setAddState(e.target.value)} className="text-sm border border-zinc-300 rounded px-2 py-1.5">
                  <option value="GA">GA</option>
                  <option value="TN">TN</option>
                  <option value="SC">SC</option>
                  <option value="NC">NC</option>
                  <option value="AL">AL</option>
                  <option value="FL">FL</option>
                </select>
                <input data-testid="suggest-zip" value={addZip} onChange={(e) => setAddZip(e.target.value)} placeholder="ZIP" className="w-20 text-sm border border-zinc-300 rounded px-2 py-1.5" />
              </div>
              <div className="border border-zinc-200 rounded p-2 bg-zinc-50 space-y-1.5">
                <p className="text-[11px] font-semibold text-zinc-600">{pt ? 'Período da simulação (a IA não usa datas passadas)' : 'Simulation range (AI ignores past dates)'}</p>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 flex-1">
                    <label className="text-[11px] text-zinc-500 shrink-0">{pt ? 'De' : 'From'}</label>
                    <input data-testid="suggest-date-from" type="date" min={todayStr()} value={suggestFrom} onChange={(e) => setSuggestFrom(e.target.value)} className="flex-1 text-xs border border-zinc-300 rounded px-2 py-1" />
                  </div>
                  <div className="flex items-center gap-1 flex-1">
                    <label className="text-[11px] text-zinc-500 shrink-0">{pt ? 'Até' : 'To'}</label>
                    <input data-testid="suggest-date-to" type="date" min={suggestFrom || todayStr()} value={suggestTo} onChange={(e) => setSuggestTo(e.target.value)} className="flex-1 text-xs border border-zinc-300 rounded px-2 py-1" />
                  </div>
                </div>
              </div>
              <Button data-testid="suggest-run-btn" onClick={runSuggest} disabled={suggesting} className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
                {suggesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-3.5 w-3.5 mr-1" />}
                {pt ? 'Simular com IA' : 'Simulate with AI'}
              </Button>

              {suggestError && <p className="text-xs text-red-600" data-testid="suggest-error">{suggestError}</p>}

              {suggestion && (
                <div className="pt-2 border-t border-zinc-100 space-y-3" data-testid="suggest-results">
                  <p className="text-[11px] text-zinc-500">{suggestion.candidate?.formatted}</p>

                  {suggestion.ai_summary && (
                    <div className="bg-indigo-50 border border-indigo-100 rounded p-2 text-xs text-indigo-900 whitespace-pre-line" data-testid="suggest-ai-summary">
                      {suggestion.ai_summary}
                    </div>
                  )}

                  {suggestion.suggestions && suggestion.suggestions.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-zinc-700 mb-1">{pt ? 'Melhores opções (clique para ver a rota)' : 'Best options (click to see the route)'}</p>
                      <ul className="space-y-1">
                        {suggestion.suggestions.map((s, i) => {
                          const isSel = selectedSuggestion && selectedSuggestion.team === s.team && selectedSuggestion.date === s.date;
                          return (
                          <li key={`${s.team}-${s.date}`}>
                            <button
                              type="button"
                              data-testid={`suggest-option-${s.team}-${s.date}`}
                              onClick={() => { setSuggestRouteInfo(null); setSelectedSuggestion(isSel ? null : s); }}
                              className={`w-full flex items-center justify-between text-xs rounded px-2 py-1.5 border transition-colors text-left ${isSel ? 'bg-violet-50 border-violet-300 ring-1 ring-violet-300' : 'bg-zinc-50 border-transparent hover:bg-zinc-100'}`}
                            >
                              <span className="flex items-center gap-1">
                                <span>{medal(i)}</span>
                                <span style={{ width: 8, height: 8, borderRadius: 999, background: colorForTeam(s.team), display: 'inline-block' }} />
                                <span className="font-semibold">{s.team}</span>
                                <span className="text-zinc-500">· {s.weekday} {fmtDate(s.date)}</span>
                              </span>
                              <span className="text-zinc-600 flex items-center gap-1">
                                {s.drive_minutes ? `~${s.drive_minutes} min` : `${s.distance_mi} mi`}
                                <Route className={`h-3.5 w-3.5 ${isSel ? 'text-violet-600' : 'text-zinc-400'}`} />
                              </span>
                            </button>
                            {isSel && (
                              <div className="mt-1 mb-0.5 mx-1 text-[11px] text-violet-800 bg-violet-50 border border-violet-100 rounded px-2 py-1" data-testid="suggest-route-info">
                                <div>🏠 {pt ? 'Endereço' : 'Address'} → {s.team} ({s.nearest_customer || (pt ? 'trabalho próximo' : 'nearby job')})</div>
                                {suggestRouteInfo?.error ? (
                                  <div className="text-red-600">{suggestRouteInfo.error === 'REQUEST_DENIED'
                                    ? (pt ? '⚠️ Habilite a Directions API.' : 'Enable Directions API.')
                                    : `${pt ? 'Erro' : 'Error'}: ${suggestRouteInfo.error}`}</div>
                                ) : suggestRouteInfo ? (
                                  <div>🚗 {suggestRouteInfo.miles} mi · ⏱️ ~{fmtDuration(suggestRouteInfo.minutes)}</div>
                                ) : (
                                  <div className="flex items-center gap-1 text-violet-600"><Loader2 className="h-3 w-3 animate-spin" />{pt ? 'Calculando rota...' : 'Calculating...'}</div>
                                )}
                              </div>
                            )}
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-500">{pt ? 'Nenhuma equipe com trabalhos próximos nesse período. Tente ampliar o intervalo de datas.' : 'No teams with nearby jobs in this range.'}</p>
                  )}

                  {suggestion.per_team && Object.keys(suggestion.per_team).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-zinc-700 mb-1">{pt ? 'Todas as opções por equipe' : 'All options per team'}</p>
                      <div className="space-y-1">
                        {Object.entries(suggestion.per_team).sort((a, b) => (a[1][0]?.distance_mi || 999) - (b[1][0]?.distance_mi || 999)).map(([team, days]) => (
                          <div key={team} className="text-xs flex items-start gap-1">
                            <span className="flex items-center gap-1 font-semibold shrink-0">
                              <span style={{ width: 8, height: 8, borderRadius: 999, background: colorForTeam(team), display: 'inline-block' }} />{team}:
                            </span>
                            <span className="text-zinc-600">
                              {days.map((d) => `${d.weekday} ${fmtDate(d.date)} (${d.distance_mi}mi)`).join(' · ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-amber-600">{pt ? '⚠️ Isto é apenas uma simulação — nenhum trabalho é criado.' : '⚠️ Simulation only — no job is created.'}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleMapModal;
